import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useGetChassisDropdownListWithExternalQuery } from '../../api';
import { useLanguage } from '../../contexts/LanguageContext';

const normalizeText = (str) =>
    (str || '')
        .replace(/\s+/g, ' ')
        .replace(/\u00A0/g, ' ')
        .trim();

const formatLicensePlate = (plate) => {
    if (!plate) return '';
    return plate.replace(/\s+/g, '').toUpperCase();
};

const ChassisWithExternalDropdown = ({ value, onChange, className, disabled, onResolvedSelected, showDescription = true }) => {
    const { getCurrentLanguageId, th } = useLanguage();
    const [selectedChassis, setSelectedChassis] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterValue, setFilterValue] = useState(''); 
    const [specificSearch, setSpecificSearch] = useState('');
    const debounceTimerRef = useRef(null);

    const languageId = getCurrentLanguageId();

    const formattedSearchText = useMemo(() => {
        return formatLicensePlate(searchText || specificSearch);
    }, [searchText, specificSearch]);

    const { data: chassisList = [], isLoading: isLoadingChassis } = useGetChassisDropdownListWithExternalQuery({
        RowCount: 100,
        SearchText: formattedSearchText,
        languageId: languageId
    });

    const chassisOptions = useMemo(() => {
        return chassisList.map((chassis) => {
            return {
                id: chassis.id,
                text: chassis.id,
                rawText: normalizeText(chassis.translatedText),
                licensePlate: chassis.licensePlate || chassis.plateNumber || null,
                motorNumber: chassis.motorNumber || null,
                fullData: chassis,
                searchableText: `${chassis.id} ${chassis.licensePlate || chassis.plateNumber || ''}`.toLowerCase()
            };
        });
    }, [chassisList]);

    const handleSelectionChange = (e) => {
        const selected = e.value;
        setSelectedChassis(selected);

        setFilterValue('');
        setSearchText('');
        setSpecificSearch('');

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        const fullData = selected?.fullData;
        onChange(selected ? selected.id : '', fullData);
    };

    const handleFilterInputChange = (value) => {
        setFilterValue(value);

        if (value && specificSearch) {
            setSpecificSearch('');
        }

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            setSearchText(value);
        }, 500);
    };

    const filterTemplate = () => (
        <div className="p-dropdown-filter-container">
            <InputText
                value={filterValue}
                onChange={(e) => handleFilterInputChange(e.target.value)}
                className="p-dropdown-filter w-full"
                placeholder={th('common.search')}
                autoFocus
                type="text"
                inputMode="text"
            />
            <span className="p-dropdown-filter-icon pi pi-search" />
        </div>
    );

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!value) {
            if (specificSearch) setSpecificSearch('');
            return;
        }

        if (isLoadingChassis) return;

        const targetId = typeof value === 'string' ? value : value?.id;
        if (!targetId) return;

        const exists = chassisOptions.some((opt) => opt.id === targetId);

        if (!exists && chassisOptions.length > 0 && !specificSearch) {
            setSpecificSearch(targetId);
        } else if (exists && specificSearch && specificSearch !== targetId) {
            setSpecificSearch('');
        }
    }, [value, chassisOptions, isLoadingChassis, specificSearch]);

    useEffect(() => {
        if (!value || chassisOptions.length === 0) {
            setSelectedChassis(null);
            return;
        }

        const matched = chassisOptions.find((opt) => {
            if (typeof value === 'string') return opt.id === value;
            if (typeof value === 'object') return opt.id === value?.id;
            return false;
        });

        setSelectedChassis(matched || null);
    }, [value, chassisOptions]);

    const onResolvedSelectedRef = useRef(onResolvedSelected);
    onResolvedSelectedRef.current = onResolvedSelected;

    useEffect(() => {
        if (selectedChassis) {
            onResolvedSelectedRef.current?.(selectedChassis.fullData);
        }
    }, [selectedChassis]);

    const itemTemplate = (opt) =>
        showDescription ? (
            <div className="flex flex-column">
                <span className="font-medium">{opt.id}</span>
                <small className="text-500">{opt.rawText}</small>
            </div>
        ) : (
            <span className="font-medium">{opt.id}</span>
        );

    const selectedItemTemplate = (opt) =>
        opt ? (
            showDescription ? (
                <span>
                    {opt.id} - {opt.rawText}
                </span>
            ) : (
                <span>{opt.id}</span>
            )
        ) : (
            <span className="text-500">{th('warrantyClaims.detail.form.chassisPlaceholder')}</span>
        );

    return (
        <Dropdown
            value={selectedChassis}
            options={chassisOptions}
            onChange={handleSelectionChange}
            optionLabel="text"
            dataKey="id"
            filter
            filterTemplate={filterTemplate}
            showClear
            className={className}
            placeholder={th('warrantyClaims.detail.form.chassisPlaceholder')}
            loading={isLoadingChassis}
            disabled={disabled}
            itemTemplate={itemTemplate}
            valueTemplate={selectedItemTemplate}
        />
    );
};

export default ChassisWithExternalDropdown;